import { Dialog as BaseDialog, Transition } from "@headlessui/react";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useIntersectionObserver } from "usehooks-ts";
import { FullPageDialogContext } from "./FullPageDialogContext";
import { useFullPageDialogContext } from "./useFullPageDialogContext";
import { Icon } from "../Icon";
import type { IconType } from "../Icon";
import { PageIndicator } from "./PageIndicator";
import { FullPageDialogLeaveTime } from "./dialogConstants";
import { H5 } from "../Header";
import { zLayer } from "../../zIndex";
import { FloatingLayerProvider } from "../../floatingLayer";

export const DialogRoot = ({
  active,
  children,
  onDismiss,
  className,
  initPage,
  fullHeight,
  dialogBackdropClassName,
  dialogClassName,
  onPageChange,
}: {
  active: boolean;
  onDismiss?: () => void;
  children: ReactNode;
  className?: string;
  initPage?: number;
  fullHeight?: boolean;
  dialogBackdropClassName?: string;
  dialogClassName?: string;
  onPageChange?: (pageNum: number | undefined) => void;
}) => {
  const [page, setPage] = useState(initPage);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  };

  // Reset page while closed so reopening starts on initPage without an effect.
  if (!active && page !== initPage) {
    setPage(initPage);
  }

  useEffect(() => {
    if (onPageChange) {
      onPageChange(page);
    }
  }, [onPageChange, page]);

  return (
    <Transition show={active} as={Fragment}>
      <BaseDialog onClose={() => onDismiss?.()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave={`ease-in duration-${FullPageDialogLeaveTime}`}
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={twMerge(
              `fixed inset-0 ${zLayer.dialog} bg-black/30`,
              dialogBackdropClassName,
            )}
            aria-hidden="true"
          />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="translate-y-full"
          enterTo="translate-y-0"
          leave="ease-in duration-200"
          leaveFrom="translate-y-0"
          leaveTo="translate-y-full"
        >
          <div
            className={twMerge(
              `fixed inset-0 ${zLayer.dialog} flex items-center justify-center`,
              dialogClassName,
            )}
          >
            <div className="flex h-full items-center justify-center">
              <BaseDialog.Panel
                ref={scrollContainerRef}
                className={twMerge(
                  "flex h-full w-screen max-w-[700px] flex-col overflow-auto rounded-lg bg-white dark:border dark:border-zinc-800 dark:bg-zinc-900",
                  fullHeight ? "md:h-[85vh]" : "md:h-min md:max-h-[85vh]",
                  className,
                )}
              >
                <FullPageDialogContext.Provider
                  value={{
                    page,
                    setPage,
                    active,
                    onDismiss,
                    fullHeight: fullHeight ?? false,
                    scrollToTop,
                  }}
                >
                  <FloatingLayerProvider host="dialog">
                    {children}
                  </FloatingLayerProvider>
                </FullPageDialogContext.Provider>
              </BaseDialog.Panel>
            </div>
          </div>
        </Transition.Child>
      </BaseDialog>
    </Transition>
  );
};

export const DialogNavigation = ({
  leftIcon,
  onClickLeftIcon,
  title,
  description,
  onClickTitle,
  children,
  className,
  leftIconClassName,
  titleClassName,
  descriptionClassName,
}: {
  leftIcon?: keyof typeof IconType;
  onClickLeftIcon?: () => void;
  title?: string;
  description?: string;
  onClickTitle?: () => void;
  children?: ReactNode;
  className?: string;
  leftIconClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  strikethrough?: string;
}) => {
  const [ref, isIntersecting] = useIntersectionObserver({});

  return (
    <>
      <div ref={ref} />
      <div
        className={twMerge(
          "sticky top-0 z-10 flex flex-col gap-4 bg-white p-4 dark:border-b dark:border-zinc-800 dark:bg-zinc-900",
          !isIntersecting && "shadow-lg",
          className,
        )}
      >
        <div
          className={
            "grid grid-cols-[24px_1fr_24px] content-center items-center justify-items-center"
          }
        >
          {leftIcon && onClickLeftIcon && leftIcon !== "MENU" ? (
            <div onClick={onClickLeftIcon}>
              <Icon
                size={1}
                icon={leftIcon}
                className={twMerge(
                  "cursor-pointer dark:text-zinc-500 dark:hover:text-zinc-100",
                  leftIconClassName,
                )}
              />
            </div>
          ) : (
            <div />
          )}
          {title ? (
            <H5
              className={twMerge(
                "text-center text-lg dark:text-zinc-100",
                onClickTitle && "cursor-pointer",
                titleClassName,
              )}
              onClick={onClickTitle}
            >
              <BaseDialog.Title as={"span"}>{title}</BaseDialog.Title>
            </H5>
          ) : (
            <div />
          )}
          <div />
        </div>
        {description ? (
          <div
            className={twMerge(
              "text-center text-gray-500 dark:text-zinc-400",
              descriptionClassName,
            )}
          >
            <BaseDialog.Description>{description}</BaseDialog.Description>
          </div>
        ) : (
          <div />
        )}
        {children}
      </div>
    </>
  );
};

export const DialogContent = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div className={twMerge("px-4 pb-4 dark:text-zinc-100", className)}>
      {children}
    </div>
  );
};

export const DialogNewFeatureHeader = ({
  imageSrc,
  onClickLeftIcon,
}: {
  imageSrc: string;
  onClickLeftIcon?: () => void;
}) => {
  return (
    <div className={"bg-zinc-100 px-8 dark:bg-zinc-800/50"}>
      {onClickLeftIcon && (
        <Icon
          size={1}
          className={"mt-4 -ml-4 cursor-pointer"}
          icon={"CHEVRON_DOWN"}
          onClick={onClickLeftIcon}
        />
      )}
      <div className={"flex items-center justify-center"}>
        <img src={imageSrc} className={"mt-auto aspect-auto"} />
      </div>
    </div>
  );
};

export const DialogNewFeatureContent = ({
  title,
  featureHeader,
  description,
}: {
  title: string;
  featureHeader?: string;
  description: string;
}) => {
  const featureHeaderText = featureHeader ?? "NEW FEATURE";
  return (
    <div className={"flex flex-col p-8"}>
      <div>
        <div
          className={
            "inline-flex items-center gap-2 rounded-sm bg-purple-100 px-2 py-1 text-sm font-semibold dark:bg-purple-900/30"
          }
        >
          <p className={"text-sm text-[#B557F1]"}>{featureHeaderText}</p>
        </div>
      </div>
      <H5 className={"mt-4 dark:text-zinc-100"}>{title}</H5>
      <p className={"mt-2 dark:text-zinc-400"}>{description}</p>
    </div>
  );
};

export const DialogNewFeaturePageIndicator = ({
  pageLength,
  activePageIndex,
}: {
  pageLength: number;
  activePageIndex: number;
}) => {
  return (
    <div className="mt-8 flex flex-row items-center justify-center gap-2">
      <PageIndicator
        pageLength={pageLength}
        activePageIndex={activePageIndex}
      />
    </div>
  );
};

export const DialogCTA = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const { fullHeight } = useFullPageDialogContext();
  const [ref, isIntersecting] = useIntersectionObserver({ threshold: 0 });

  return (
    <>
      <div
        className={twMerge(
          "mt-auto flex w-full items-end justify-end space-x-3 p-4 pt-3 dark:border-t dark:border-zinc-800 dark:bg-zinc-900/50",
          fullHeight && "sticky bottom-0 bg-white dark:bg-zinc-900",
          fullHeight &&
            !isIntersecting &&
            "drop-shadow-[0_-4px_3px_rgba(0,0,0,0.1)] drop-shadow-[0_-10px_8px_rgba(0,0,0,0.04)]",
          className,
        )}
      >
        {children}
      </div>
      <div ref={ref} />
    </>
  );
};

export const DialogPage = ({
  page,
  children,
}: {
  page: number;
  children: ReactNode;
}) => {
  const { page: currentPage } = useFullPageDialogContext();

  if (page !== currentPage) return null;

  return <>{children}</>;
};

export const Dialog = DialogRoot;
